import { createElement, useState } from 'react';
import { DEFAULT_PROJECT_ICON, PROJECT_ICONS, type ProjectIconName } from '@shared/project-icon';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { PROJECT_ICON_COMPONENTS, PROJECT_ICON_LABELS, projectIconComponent } from './project-icons';

/**
 * A project's icon (T-25): one of PROJECT_ICONS, drawn in the project's colour (T-18). Like the
 * colour, it means "which project", never a status. Decorative: the project's name sits beside it.
 */
export function ProjectIcon({ icon, colour, className }: { icon: ProjectIconName | null; colour?: string; className?: string }) {
  return createElement(projectIconComponent(icon), {
    'aria-hidden': true,
    className: cn('size-4 shrink-0', className),
    style: colour ? { color: colour } : undefined,
  });
}

/**
 * Pick a project's icon: a button showing the current one that opens a grid of PROJECT_ICONS, in
 * the project's colour. `null` is the default folder icon. Inside a FormField it takes the field's id.
 */
export function ProjectIconField({
  value,
  onChange,
  colour,
  id,
  disabled,
}: {
  value: ProjectIconName | null;
  onChange: (value: ProjectIconName | null) => void;
  colour?: string;
  id?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const current = value ?? DEFAULT_PROJECT_ICON;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" className="w-full justify-start gap-2" disabled={disabled}>
          <ProjectIcon icon={current} colour={colour} />
          <span className="truncate">{PROJECT_ICON_LABELS[current]}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <div role="radiogroup" aria-label="Project icon" className="grid grid-cols-8 gap-1">
          {PROJECT_ICONS.map((name) => {
            const Icon = PROJECT_ICON_COMPONENTS[name];
            const selected = name === current;
            return (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={PROJECT_ICON_LABELS[name]}
                data-hint={PROJECT_ICON_LABELS[name]}
                className={cn(
                  'flex size-8 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected && 'bg-accent ring-2 ring-primary'
                )}
                onClick={() => {
                  onChange(name === DEFAULT_PROJECT_ICON ? null : name);
                  setOpen(false);
                }}
              >
                <Icon aria-hidden className="size-4" style={colour ? { color: colour } : undefined} />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
